import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { I18nProvider } from "./i18n/I18nContext";
import { SiteContentProvider, useSiteContent } from "./i18n/SiteContent";
import { Toaster } from "./components/ui/sonner";

import Header from "./sections/Header";
import Hero from "./sections/Hero";
import About from "./sections/About";
import Brands from "./sections/Brands";
import Highlights from "./sections/Highlights";
import Services from "./sections/Services";
import Industries from "./sections/Industries";
import Faq from "./sections/Faq";
import StoryTimeline from "./sections/StoryTimeline";
import ClientsMarquee from "./sections/ClientsMarquee";
import Footer from "./sections/Footer";

import Chatbot from "./widgets/Chatbot";

import ProductDetail from "./pages/ProductDetail";
import ServiceDetail from "./pages/ServiceDetail";
import EditorialPage from "./pages/EditorialPage";
import OurCoreTeam from "./pages/OurCoreTeam";
import ClientsPortfolio from "./pages/ClientsPortfolio";
import NotFound from "./pages/NotFound";
import ScrollManager from "./widgets/ScrollManager";
import NavProgress from "./widgets/NavProgress";
import ScrollTopButton from "./widgets/ScrollTopButton";

import { trackPageView } from "./lib/analytics";

// Admin
import { AdminAuthProvider } from "./admin/AuthContext";
import AdminLogin from "./admin/Login";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/Dashboard";
import BlogManager from "./admin/BlogManager";
import TeamManager from "./admin/TeamManager";
import JobsManager from "./admin/JobsManager";
import PageContentManager from "./admin/PageContentManager";
import SettingsManager from "./admin/SettingsManager";
import LeadsManager from "./admin/LeadsManager";
import Analytics from "./admin/Analytics";
import SliderManager from "./admin/SliderManager";
import BrandsManager from "./admin/BrandsManager";
import HomeSectionsManager from "./admin/HomeSectionsManager";
import CoreTeamManager from "./admin/CoreTeamManager";
import ClientLogosManager from "./admin/ClientLogosManager";
import DetailPagesManager from "./admin/DetailPagesManager";
import ClientProjectsManager from "./admin/ClientProjectsManager";

const SECTIONS = ["hero", "about", "story", "products", "highlights", "services", "industries", "faq", "clients"];

const Landing = () => {
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .forEach((e) => {
            setActiveSection(e.target.id);
            trackPageView(e.target.id);
          });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.title = "OpSysDigital — Digital Marketing, IT Consulting & AI Automation in Dubai, UAE";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header activeSection={activeSection} />
      <main>
        <LandingSections />
      </main>
      <Footer />
    </div>
  );
};

/**
 * Global chatbot — mounts on every public route (skipped inside the admin CMS).
 * Uses SiteContentProvider so the chatbot can read any needed CMS content.
 */
function GlobalChatbot() {
  const location = useLocation();
  if (location.pathname.startsWith("/admin")) return null;
  return (
    <I18nProvider>
      <SiteContentProvider>
        <Chatbot />
      </SiteContentProvider>
    </I18nProvider>
  );
}

/**
 * Landing sections — order + visibility driven by CMS key `home.sections`.
 * Admins can toggle any section on/off from `/admin/home-sections`.
 * The default (falls back if CMS not set) matches the desired 5-section layout:
 *   1) Hero  2) Story  3) Brands  4) Services (What we do)  5) Highlights (Trusted across regions)
 */
function LandingSections() {
  const { pg } = useSiteContent();
  const rawStr = pg("home.sections", null);
  const config = (() => {
    if (rawStr && typeof rawStr === "string") {
      try { return JSON.parse(rawStr); } catch { /* fall through */ }
    }
    if (rawStr && typeof rawStr === "object") return rawStr;
    return null;
  })();

  const DEFAULT = [
    { key: "hero",       label: "Hero Banner",           visible: true,  Component: Hero },
    { key: "story",      label: "Our Story",             visible: true,  Component: StoryTimeline },
    { key: "brands",     label: "Brands We Represent",   visible: true,  Component: Brands },
    { key: "services",   label: "What We Do",            visible: true,  Component: Services },
    { key: "highlights", label: "Trusted Across Regions",visible: true,  Component: Highlights },
    { key: "about",      label: "About OpSys",           visible: false, Component: About },
    { key: "industries", label: "Industries We Serve",   visible: false, Component: Industries },
    { key: "faq",        label: "FAQ",                   visible: false, Component: Faq },
    { key: "clients",    label: "Trusted by Clients",    visible: false, Component: ClientsMarquee },
  ];

  // Merge CMS order + visibility onto the defaults
  const ordered = (() => {
    if (!config || !Array.isArray(config.order)) return DEFAULT;
    const map = Object.fromEntries(DEFAULT.map((s) => [s.key, s]));
    const seen = new Set();
    const list = [];
    for (const key of config.order) {
      if (map[key] && !seen.has(key)) {
        const vis = config.visible && Object.prototype.hasOwnProperty.call(config.visible, key)
          ? !!config.visible[key]
          : map[key].visible;
        list.push({ ...map[key], visible: vis });
        seen.add(key);
      }
    }
    // Append any missing defaults at the end (hidden by default)
    for (const s of DEFAULT) {
      if (!seen.has(s.key)) {
        const vis = config.visible && Object.prototype.hasOwnProperty.call(config.visible, s.key)
          ? !!config.visible[s.key]
          : s.visible;
        list.push({ ...s, visible: vis });
      }
    }
    return list;
  })();

  return (
    <>
      {ordered.filter((s) => s.visible).map((s) => {
        const C = s.Component;
        return <C key={s.key} />;
      })}
    </>
  );
}

function RevealObserver() {
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    const observeAll = () => document.querySelectorAll(".reveal:not(.in), .anim:not(.in)").forEach((el) => io.observe(el));
    observeAll();
    const revealVisible = () => {
      document.querySelectorAll(".reveal:not(.in), .anim:not(.in)").forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight - 40 && r.bottom > 0) el.classList.add("in");
      });
    };
    // Multi-pass safety net — force-reveal visible elements after mount
    const timers = [80, 250, 600, 1200].map((t) => setTimeout(() => { observeAll(); revealVisible(); }, t));
    const onScroll = () => revealVisible();
    window.addEventListener("scroll", onScroll, { passive: true });
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) => m.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.classList?.contains?.("anim") || node.classList?.contains?.("reveal")) io.observe(node);
          node.querySelectorAll?.(".anim, .reveal").forEach((el) => io.observe(el));
        }
      }));
      revealVisible();
    });
    mo.observe(document.body, { subtree: true, childList: true });
    return () => {
      io.disconnect(); mo.disconnect();
      timers.forEach(clearTimeout);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RevealObserver />
      <ScrollManager />
      <NavProgress />
      <ScrollTopButton />
      <GlobalChatbot />
      <Routes>
        {/* Admin routes */}
        <Route
          path="/admin/login"
          element={
            <AdminAuthProvider>
              <AdminLogin />
            </AdminAuthProvider>
          }
        />
        <Route
          path="/admin/*"
          element={
            <AdminAuthProvider>
              <AdminLayout />
            </AdminAuthProvider>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="blog" element={<BlogManager />} />
          <Route path="team" element={<TeamManager />} />
          <Route path="jobs" element={<JobsManager />} />
          <Route path="content" element={<PageContentManager />} />
          <Route path="detail-pages" element={<DetailPagesManager />} />
          <Route path="client-projects" element={<ClientProjectsManager />} />
          {/* Legacy admin route retired — redirect bookmarked URLs. */}
          <Route path="portfolio" element={<Navigate to="/admin/client-projects" replace />} />
          <Route path="home-sections" element={<HomeSectionsManager />} />
          <Route path="core-team" element={<CoreTeamManager />} />
          <Route path="client-logos" element={<ClientLogosManager />} />
          <Route path="slider" element={<SliderManager />} />
          <Route path="brands" element={<BrandsManager />} />
          <Route path="settings" element={<SettingsManager />} />
          <Route path="leads" element={<LeadsManager />} />
          <Route path="analytics" element={<Analytics />} />
        </Route>

        {/* Public product detail */}
        <Route
          path="/products/:slug"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <ProductDetail />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* Public service detail — main pages */}
        <Route
          path="/services/:slug"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <ServiceDetail />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* Public service detail — sub-service pages */}
        <Route
          path="/services/:parent/:slug"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <ServiceDetail />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* Editorial pages — Who We Are, What We Do, Why Choose Us */}
        {["who-we-are", "what-we-do", "why-choose-us"].map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <I18nProvider>
                <SiteContentProvider>
                  <EditorialPage />
                  <Toaster position="top-right" richColors />
                </SiteContentProvider>
              </I18nProvider>
            }
          />
        ))}

        {/* Our Core Team */}
        <Route
          path="/our-core-team"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <OurCoreTeam />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* Clients Portfolio */}
        <Route
          path="/clients-portfolio"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <ClientsPortfolio />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* Public landing — exact `/` only */}
        <Route
          path="/"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <Landing />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />

        {/* 404 catch-all — must be last */}
        <Route
          path="*"
          element={
            <I18nProvider>
              <SiteContentProvider>
                <NotFound />
                <Toaster position="top-right" richColors />
              </SiteContentProvider>
            </I18nProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
